/**
 * Searchable contact/payee picker used by the bank-transaction
 * classification panel (contribution and expenditure paths).
 *
 * A native `<select>` is unusable once a campaign has more than a
 * handful of contacts — you can't type to find one. This wraps the
 * shadcn Popover + cmdk Command primitives into a combobox that filters
 * by name as you type, while presenting the same closed-state affordance
 * (full-width bordered control showing the current selection).
 */
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { FinanceContactDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface ContactComboboxProps {
  /** Ties the trigger to an external `<label htmlFor>`. */
  id?: string;
  /** Accessible name when there's no associated `<label htmlFor>`. */
  ariaLabel?: string;
  /** Selected contact id, or `''` when nothing is chosen. */
  value: string;
  onChange: (contactId: string) => void;
  contacts: FinanceContactDto[];
  placeholder?: string;
  /** Placeholder inside the search box. */
  searchPlaceholder?: string;
  /** Shown when the filter matches no contacts. */
  emptyText?: string;
  /** Marks one contact as the system suggestion (BSW-005). */
  suggestedContactId?: string | null;
  /**
   * People who have not given, surfaced when a search matches no one in `contacts`
   * (see `useDonorPickerContacts`). Rendered as their own group so a prospect is never
   * mistaken for a donor.
   */
  prospects?: FinanceContactDto[];
  /** Reports the search text as the user types. */
  onSearchChange?: (search: string) => void;
}

export function ContactCombobox({
  id,
  ariaLabel,
  value,
  onChange,
  contacts,
  placeholder = 'Select a contact...',
  searchPlaceholder = 'Search contacts...',
  emptyText = 'No contacts found.',
  suggestedContactId,
  prospects = [],
  onSearchChange,
}: ContactComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = contacts.find((c) => c.id === value) ?? prospects.find((c) => c.id === value);

  const updateSearch = (next: string) => {
    setSearch(next);
    onSearchChange?.(next);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) updateSearch('');
  };

  const handleSelect = (contactId: string) => {
    onChange(contactId);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn(
            'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        // Radix portals this content to document.body, but React bubbles its
        // events through the React tree. Without this, a picked contact's click
        // reaches ancestor click handlers (e.g. the TriageLedger row disclosure
        // toggle), collapsing the surrounding panel and dropping the selection.
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={updateSearch} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {contacts.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  keywords={[c.name]}
                  onSelect={() => handleSelect(c.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === c.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">
                    {c.name}
                    {c.id === suggestedContactId ? ' (Suggested)' : ''}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {prospects.length > 0 && (
              <CommandGroup heading="Not yet given">
                {prospects.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.id}
                    keywords={[c.name]}
                    onSelect={() => handleSelect(c.id)}
                    className="text-muted-foreground"
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="truncate">{c.name}</span>
                    <span className="ml-auto shrink-0 text-xs">Prospect</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
